import React from 'react';
import { ChangeEvent, FormEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { submitApplication } from '../../services/api';
import './pStyles/Applicationform.css';

interface FormData {
  firstname: string;
  surname: string;
  phone: string;
  email: string;
  location: string;
  otherLocation: string;
  availability: string;
  availabilityDescription: string;
  profession: string;
  otherProfession: string;
  dataConsent: boolean;
  termsAccepted: boolean;
  resume_url?: File | null;
}

interface Skill {
  id: number;
  name: string;
  description?: string;
}

interface Location {
  id: number;
  name: string;
  description?: string;
}

export default function ApplicationForm() {
  const [formData, setFormData] = useState<FormData>({
    firstname: '',
    surname: '',
    phone: '',
    email: '',
    location: '',
    otherLocation: '',
    availability: '',
    availabilityDescription: '',
    profession: '',
    otherProfession: '',
    dataConsent: false,
    termsAccepted: false,
    resume_url: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();

  const API_BASE_URL = 'https://mellow-commitment-production-bf3d.up.railway.app';

  // Fetch skills and locations from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const [skillsResponse, locationsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/skills`),
          fetch(`${API_BASE_URL}/api/locations`)
        ]);

        if (skillsResponse.ok) {
          const skills = await skillsResponse.json();
          setAvailableSkills(skills);
        }

        if (locationsResponse.ok) {
          const locations = await locationsResponse.json();
          setAvailableLocations(locations);
        }
      } catch (error) {
        console.error('Error fetching skills and locations:', error);
        // Fallback to hardcoded data if API fails
        setAvailableSkills([
          { id: 1, name: 'Business Analyst' },
          { id: 2, name: 'Accountant' },
          { id: 3, name: 'Plumber' },
          { id: 4, name: 'Construction Worker' },
          { id: 5, name: 'Architect' },
          { id: 6, name: 'Mechanical Engineer' },
          { id: 7, name: 'Industrial Engineer' },
          { id: 8, name: 'Data Analyst/Engineer' },
          { id: 9, name: 'Software Developer' },
          { id: 10, name: 'Project Manager' },
          { id: 11, name: 'Marketing Specialist' },
          { id: 12, name: 'Graphic Designer' },
          { id: 13, name: 'Teacher/Educator' },
          { id: 14, name: 'Healthcare Professional' },
          { id: 15, name: 'Legal Professional' },
          { id: 16, name: 'Environmental Specialist' },
          { id: 17, name: 'Social Worker' }
        ]);
        setAvailableLocations([
          { id: 1, name: 'Cape Town CBD' },
          { id: 2, name: 'Bellville' },
          { id: 3, name: 'Rondebosch' },
          { id: 4, name: 'Claremont' },
          { id: 5, name: 'Durbanville' },
          { id: 6, name: 'Paarl' },
          { id: 7, name: 'Stellenbosch' },
          { id: 8, name: 'Milnerton' }
        ]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Function to add new skill
  const handleAddNewSkill = async (skillName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/skills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Public user ID for applications
        },
        body: JSON.stringify({ name: skillName.trim() })
      });

      if (response.ok) {
        const newSkillData = await response.json();
        setAvailableSkills(prev => [...prev, newSkillData].sort((a, b) => a.name.localeCompare(b.name)));
        return newSkillData.name;
      } else {
        console.error('Failed to add new skill');
        return skillName; // Return original name if API fails
      }
    } catch (error) {
      console.error('Error adding new skill:', error);
      return skillName; // Return original name if API fails
    }
  };

  // Function to add new location
  const handleAddNewLocation = async (locationName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Public user ID for applications
        },
        body: JSON.stringify({ name: locationName.trim() })
      });

      if (response.ok) {
        const newLocationData = await response.json();
        setAvailableLocations(prev => [...prev, newLocationData].sort((a, b) => a.name.localeCompare(b.name)));
        return newLocationData.name;
      } else {
        console.error('Failed to add new location');
        return locationName; // Return original name if API fails
      }
    } catch (error) {
      console.error('Error adding new location:', error);
      return locationName; // Return original name if API fails
    }
  };

  const professions = [
    'Business Analyst',
    'Accountant', 
    'Plumber',
    'Construction Worker',
    'Architect',
    'Mechanical Engineer',
    'Industrial Engineer',
    'Data Analyst/Engineer',
    'Software Developer',
    'Project Manager',
    'Marketing Specialist',
    'Graphic Designer',
    'Teacher/Educator',
    'Healthcare Professional',
    'Legal Professional',
    'Environmental Specialist',
    'Social Worker',
    'Other'
  ];

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Clear email error when user starts typing
    if (name === 'email') {
      setEmailError(null);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        resume_url: e.target.files![0]
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Basic validation
    if (!formData.firstname || !formData.surname || !formData.email || 
        !formData.phone || !formData.location || !formData.profession || 
        !formData.availability || !formData.dataConsent || !formData.termsAccepted) {
      setError('Please fill in all required fields and accept the terms.');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.profession === 'Other' && !formData.otherProfession) {
      setError('Please specify your profession.');
      setIsSubmitting(false);
      return;
    }

    if (formData.location === 'Other' && !formData.otherLocation) {
      setError('Please specify your location.');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.availability === 'Describe your availability' && !formData.availabilityDescription) {
      setError('Please describe your availability.');
      setIsSubmitting(false);
      return;
    }
    
    // Phone validation (South African format)
    const phoneRegex = /^(\+27|0)[0-9]{9}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      setError('Please enter a valid South African phone number (e.g., +27 82 123 4567 or 082 123 4567)');
      setIsSubmitting(false);
      return;
    }

    try {
      // Handle new skills and locations
      let finalLocation = formData.location;
      let finalSkills = formData.profession;

      // Add new location if "Other" is selected
      if (formData.location === 'Other') {
        finalLocation = await handleAddNewLocation(formData.otherLocation);
      }

      // Add new skill if "Other" is selected
      if (formData.profession === 'Other') {
        finalSkills = await handleAddNewSkill(formData.otherProfession);
      }

      const submitFormData = new FormData();
      submitFormData.append('firstname', formData.firstname);
      submitFormData.append('surname', formData.surname);
      submitFormData.append('phone', formData.phone);
      submitFormData.append('email', formData.email);
      submitFormData.append('location', finalLocation);
      
      // Handle availability - if custom description, use that, otherwise use selected option
      const finalAvailability = formData.availability === 'Describe your availability' 
        ? formData.availabilityDescription 
        : formData.availability;
      submitFormData.append('availability', finalAvailability);
      
      // Use the final skills (either from dropdown or newly added)
      submitFormData.append('skills', finalSkills);
      
      if (formData.resume_url) {
        submitFormData.append('resume', formData.resume_url);
      }

      // Submit application and wait for response
      const response = await submitApplication(submitFormData);
      
      // Navigate to status page immediately after successful submission
      if (response && response.applicationId) {
        navigate(`/application-status?applicationId=${response.applicationId}&email=${encodeURIComponent(formData.email)}`);
      }
    } catch (err: any) {
      console.error('Full error:', err);
      
      // Handle specific error cases
      if (err.response?.data?.error === 'Email already exists') {
        setEmailError('This email address is already registered. Please use a different email or contact support if you believe this is an error.');
        setError(null);
      } else {
        setError(err.message || 'Submission failed');
        setEmailError(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="application-form">
      <div className="title-with-logo">
        <img src="/skillsbridge2.jpg" alt="SkillsBridge Logo" className="title-logo" />
        <h1 className="title">SkillsBridge</h1>
      </div>
      <h3>Join our platform</h3>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* CV Upload Section */}
          <section className="form-section">
            <h2>
              <span className="section-icon">📎</span> 
              Attach CV
              <span className="optional">(Optional)</span>
            </h2>
            <div className="form-group">
              <div className="file-upload-container">
                <input 
                  type="file" 
                  id="resume" 
                  name="resume" 
                  onChange={handleFileChange} 
                  accept=".pdf,.doc,.docx"
                  className="file-input"
                />
                <label htmlFor="resume" className="file-upload-label">
                  <span className="upload-icon">📂</span>
                  <span className="upload-text">
                    {formData.resume_url ? formData.resume_url.name : 'Click to upload your CV'}
                  </span>
                  <span className="upload-note">Accepted formats: PDF, DOC, DOCX (Max 5MB)</span>
                </label>
              </div>
            </div>
          </section>

          {/* Personal Info */}
          <section className="form-section">
            <h2>Personal Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstname">First Name*</label>
                <input 
                  type="text" 
                  id="firstname" 
                  name="firstname" 
                  value={formData.firstname}
                  onChange={handleChange} 
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="surname">Surname*</label>
                <input 
                  type="text" 
                  id="surname" 
                  name="surname" 
                  value={formData.surname}
                  onChange={handleChange} 
                  required
                />
              </div>
            </div>
          </section>

          {/* Contact Info */}
          <section className="form-section">
            <h2>Contact Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="phone">
                  📞 Phone* (South African)
                </label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone}
                  onChange={handleChange} 
                  placeholder="+27 82 123 4567 or 082 123 4567"
                  required
                />
                <small className="field-hint">Enter your phone number with South African country code</small>
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  ✉️ Email*
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange} 
                  required
                />
                {emailError && <p className="error email-error">{emailError}</p>}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="location">
                📍 Location*
              </label>
              {loadingData ? (
                <div className="loading-text">Loading locations...</div>
              ) : (
                <>
                  <select 
                    id="location" 
                    name="location" 
                    value={formData.location}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Location --</option>
                    {availableLocations.map(location => (
                      <option key={location.id} value={location.name}>
                        {location.name}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                  
                  {formData.location === 'Other' && (
                    <div className="form-group">
                      <label htmlFor="otherLocation">Please specify your location*</label>
                      <input 
                        type="text" 
                        id="otherLocation" 
                        name="otherLocation" 
                        value={formData.otherLocation}
                        onChange={handleChange} 
                        placeholder="e.g., Johannesburg, Pretoria, Durban"
                        required={formData.location === 'Other'}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Professional Information */}
          <section className="form-section">
            <h2>Skills to Contribute</h2>
            <div className="form-group">
              <label htmlFor="profession">What would you like to contribute?*</label>
              {loadingData ? (
                <div className="loading-text">Loading skills...</div>
              ) : (
                <>
                  <select 
                    id="profession" 
                    name="profession" 
                    value={formData.profession}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Profession --</option>
                    {availableSkills.map(skill => (
                      <option key={skill.id} value={skill.name}>{skill.name}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>

                  {formData.profession === 'Other' && (
                    <div className="form-group">
                      <label htmlFor="otherProfession">Please describe what you would like to contribute*</label>
                      <input 
                        type="text" 
                        id="otherProfession" 
                        name="otherProfession" 
                        value={formData.otherProfession}
                        onChange={handleChange} 
                        placeholder="e.g., Photography, Event Planning, Social Media Management"
                        required={formData.profession === 'Other'}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Availability */}
          <section className="form-section">
            <h2>Availability</h2>
            <div className="form-group">
              <label htmlFor="availability">
                ⏰ When are you available?*
              </label>
              <select 
                id="availability" 
                name="availability" 
                value={formData.availability}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Availability --</option>
                <option value="Project-based">Project-based availability</option>
                <option value="Weekend only">Weekends only</option>
                <option value="Flexible">Flexible schedule</option>
                <option value="Describe your availability">Describe your availability</option>
              </select>
            </div>
            
            {formData.availability === 'Describe your availability' && (
              <div className="form-group">
                <label htmlFor="availabilityDescription">Please describe your availability*</label>
                <textarea 
                  id="availabilityDescription" 
                  name="availabilityDescription" 
                  value={formData.availabilityDescription}
                  onChange={handleChange} 
                  placeholder="e.g., Available evenings after 6 PM, or only during school holidays, or 2-3 hours on weekends"
                  rows={3}
                  required={formData.availability === 'Describe your availability'}
                />
              </div>
            )}
          </section>

          {/* Consent */}
          <section className="form-section">
            <h2>Consent & Agreement</h2>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="dataConsent"
                  checked={formData.dataConsent}
                  onChange={handleChange}
                  required
                />
                <span className="checkmark"></span>
                I understand and consent to SkillsBridge collecting, storing, and using my personal 
                data for the purpose of matching me with relevant volunteer opportunities and 
                communicating about community projects.
              </label>
            </div>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                />
                <span className="checkmark"></span>
                I have read and agree to SkillsBridge's Terms & Conditions and Privacy Policy.
              </label>
            </div>
          </section>

          {error && <p className="error">{error}</p>}
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Join SkillsBridge Community'}
          </button>
          
          <p className="note">
            We will review your application and contact you within 48 hours.
          </p>
        </form>
      </div>
    </div>
  );
}